import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Smartphone, Signal, Zap, Package, Gift, Headphones, Wifi, Search } from "lucide-react";

const ICON = { sim: Signal, phone: Smartphone, ricarica: Zap, accessorio: Headphones, servizio: Wifi, gift: Gift };

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [operator, setOperator] = useState("all");
  const [buying, setBuying] = useState(null);
  const [varProduct, setVarProduct] = useState(null);
  const [amount, setAmount] = useState("");
  const [giftProduct, setGiftProduct] = useState(null);
  const [giftEmail, setGiftEmail] = useState("");

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const tabs = [{ key: "all", label: "Tutti" }, ...categories.map((c) => ({ key: c.key, label: c.label }))];
  const operators = ["all", ...Array.from(new Set(products.map((p) => p.operator).filter(Boolean)))];

  const checkout = async (p, custom_amount, gift_email) => {
    setBuying(p.id);
    try {
      const { data } = await api.post("/payments/checkout", { product_id: p.id, quantity: 1, origin_url: window.location.origin, custom_amount, gift_email });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore checkout");
      setBuying(null);
    }
  };

  const onBuy = (p) => {
    if (p.variable_amount) { setVarProduct(p); setAmount(""); return; }
    if (p.category === "gift") { setGiftProduct(p); setGiftEmail(""); return; }
    checkout(p);
  };

  const confirmVariable = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Inserisci un importo valido"); return; }
    const p = varProduct; setVarProduct(null); checkout(p, val);
  };

  const confirmGift = () => {
    const p = giftProduct; setGiftProduct(null); checkout(p, undefined, giftEmail || undefined);
  };

  const filtered = products.filter((p) =>
    (cat === "all" || p.category === cat) &&
    (operator === "all" || p.operator === operator) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <ServiceHistoryButton service="catalogo" />
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Vendita</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Catalogo prodotti</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca prodotto…" data-testid="catalog-search-input" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={operator} onValueChange={setOperator}>
          <SelectTrigger className="sm:w-56" data-testid="catalog-operator-select"><SelectValue placeholder="Operatore" /></SelectTrigger>
          <SelectContent>
            {operators.map((o) => <SelectItem key={o} value={o}>{o === "all" ? "Tutti gli operatori" : o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={cat} onValueChange={setCat}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map((c) => <TabsTrigger key={c.key} value={c.key} data-testid={`cat-${c.key}`}>{c.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((p) => {
          const Icon = ICON[p.category] || Package;
          return (
            <Card key={p.id} className="overflow-hidden glow-border flex flex-col" data-testid={`product-card-${p.id}`}>
              <div className="h-40 bg-secondary/50 relative flex items-center justify-center">
                {p.image_url
                  ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  : <Icon className="w-14 h-14 text-primary/60" />}
                <Badge className="absolute top-3 left-3 capitalize bg-background/80 text-foreground border border-border">{p.category}</Badge>
                {p.variable_amount && <Badge className="absolute top-3 right-3 bg-amber-500 text-white border-0">Importo libero</Badge>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2">
                  {p.operator && <Badge variant="outline" className="text-xs">{p.operator}</Badge>}
                  {p.data_gb && <Badge variant="outline" className="text-xs">{p.data_gb}</Badge>}
                </div>
                <h3 className="font-head font-semibold text-lg mt-2">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 flex-1">{p.description}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-head text-2xl font-bold">{p.variable_amount ? "—" : euro(p.price)}</span>
                  <Button size="sm" disabled={buying === p.id} onClick={() => onBuy(p)} data-testid={`buy-${p.id}`}>
                    {buying === p.id ? "Attendi…" : p.variable_amount ? "Paga" : "Acquista"}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {filtered.length === 0 && <p className="text-muted-foreground">Nessun prodotto trovato.</p>}

      <Dialog open={!!varProduct} onOpenChange={(o) => !o && setVarProduct(null)}>
        <DialogContent data-testid="variable-amount-dialog">
          <DialogHeader>
            <DialogTitle>{varProduct?.name}</DialogTitle>
            <DialogDescription>Inserisci l'importo esatto da pagare per questo servizio.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Importo (€)</Label>
            <Input type="number" step="0.01" min="0.01" className="mt-1.5" data-testid="variable-amount-input"
              value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="es. 87.50" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVarProduct(null)} data-testid="variable-cancel-button">Annulla</Button>
            <Button onClick={confirmVariable} data-testid="variable-confirm-button">Procedi al pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!giftProduct} onOpenChange={(o) => !o && setGiftProduct(null)}>
        <DialogContent data-testid="gift-email-dialog">
          <DialogHeader>
            <DialogTitle>{giftProduct?.name}</DialogTitle>
            <DialogDescription>Inserisci l'email del cliente per ricevere il codice gift card (facoltativo).</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Email cliente (facoltativa)</Label>
            <Input type="email" className="mt-1.5" data-testid="gift-email-input"
              value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} placeholder="cliente@email.it" autoFocus />
            <p className="text-xs text-muted-foreground mt-2">Il codice sarà comunque mostrato a schermo dopo il pagamento.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGiftProduct(null)} data-testid="gift-cancel-button">Annulla</Button>
            <Button onClick={confirmGift} data-testid="gift-confirm-button">Procedi al pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
